import { ThunkResult } from "common/reducers";
import { DocumentActions } from "common/reducers/document.actions";
import { ImageActions } from "common/reducers/image.actions";
import { RootDispatchType } from "common/store";
import { Nullable } from "common/types";

// File.type can be empty for some drag-and-drop flows on certain browser/OS
// combos. Pair the MIME check with an extension fallback so common image
// formats still get routed correctly instead of silently falling through.
const IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".heic", ".bmp", ".tiff"];

const isImageType = (file: File): boolean => {
  if (file.type.startsWith("image/")) return true;
  const lower = file.name.toLowerCase();
  return IMAGE_EXTENSIONS.some((ext) => lower.endsWith(ext));
};

const isPdfType = (file: File): boolean => file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");

/**
 * Phase 1 of the file upload lifecycle: MIME-routes a dropped file into
 * Document/Image staging. See `specs/line-item-editor.md`.
 */
export const EstimateLineItemAsyncActions = {
  /**
   * @param sourceImageId - (images only) the job-photo this copy was cloned from, so the inspection
   *   picker can dedup within the session. Device uploads pass nothing → never deduped (correct).
   */
  addFileToLineItem: (lineItemId: number, file: File, sourceImageId?: Nullable<number>): ThunkResult<void> => {
    return (dispatch: RootDispatchType) => {
      if (isImageType(file)) {
        dispatch(
          ImageActions.estimateLineItemImage(lineItemId, {
            file_queue: file,
            name: file.name,
            display: "both",
            imageable_id: lineItemId,
            imageable_type: "EstimateLineItem",
            source_image_id: sourceImageId ?? null,
          }),
        );
        return;
      }

      if (isPdfType(file)) {
        dispatch(
          DocumentActions.estimateLineItemDocument(lineItemId, {
            file_queue: file,
            name: file.name,
            content_type: file.type || "application/pdf",
            display: "both",
            documentable_id: lineItemId,
            documentable_type: "EstimateLineItem",
          }),
        );
      }
    };
  },
};
